/**
 * firm-name-match — pure helpers for the Firmenname autocomplete (api + web).
 *
 * Zefix's company search matches the START of the whole name and returns every match for a
 * stem. The api fetches once per 3-character stem; the browser filters and ranks the rest of
 * the typed text locally. Set inclusion makes that safe: every "basl…" match is in the "bas"
 * list. A typo inside the stem fetches the wrong list and cannot be repaired here.
 *
 * Ranking tiers (best first):
 *   1. name starts with the query          (match-sorter STARTS_WITH and above)
 *   2. some word starts with the query     (match-sorter WORD_STARTS_WITH)
 *   3. every query token is a prefix of a different word, any order
 *   4. typo tolerance: each token within Damerau-Levenshtein distance 1 (4–7 chars) or
 *      2 (≥ 8 chars) of a word prefix; tokens under 4 chars must match exactly
 * Tiers 1–2 come from match-sorter; 3–4 are ours (the library has no any-order or
 * edit-distance ranking). Within a tier: shorter name first, then alphabetical.
 */

import { matchSorter, rankings } from 'match-sorter';

import { FIRM_NAME_STEM_MIN_LENGTH, LEGAL_FORM_SUFFIXES } from './firm-name.constants';

/** One slimmed Zefix search row (the api never forwards more than this). */
export interface FirmNameSuggestion {
  name: string;
  /** Formatted CHE-xxx.xxx.xxx, or null when Zefix returned none. */
  uid: string | null;
  legalSeat: string | null;
}

/** GET /register/firm-names response. */
export interface FirmNameSuggestResponse {
  stem: string;
  suggestions: FirmNameSuggestion[];
}

const LEGAL_FORM_STRIP_RE = new RegExp(`\\s(${LEGAL_FORM_SUFFIXES.join('|')})$`);

/**
 * Lowercase, strip diacritics, unify &/und/and, drop punctuation and a trailing legal form.
 * "Basler Chemie AG" → "basler chemie"; "Milk & Honey Sàrl" → "milk & honey".
 */
export function normalizeFirmName(raw: string): string {
  const s = raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/&|\bund\b|\band\b/g, ' & ')
    .replace(/[^\p{L}\p{N}&\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return s.replace(LEGAL_FORM_STRIP_RE, '').trim();
}

/**
 * The stem sent to Zefix: the shortest lowercase prefix of the trimmed input holding at least
 * 3 non-space characters ("AB Consulting" → "ab c"). Null while the input is too short.
 * Prefix-stable: every longer input with the same start yields the same stem.
 */
export function firmNameStem(raw: string): string | null {
  const t = raw.trimStart().toLowerCase();
  for (let i = FIRM_NAME_STEM_MIN_LENGTH; i <= t.length; i++) {
    const candidate = t.slice(0, i);
    if (candidate.replace(/\s/g, '').length >= FIRM_NAME_STEM_MIN_LENGTH) return candidate;
  }
  return null;
}

/** A suggestion with its normalized form, computed once per list. */
export interface PreparedFirmName {
  row: FirmNameSuggestion;
  norm: string;
  words: string[];
}

export function prepareFirmNames(rows: FirmNameSuggestion[]): PreparedFirmName[] {
  return rows.map((row) => {
    const norm = normalizeFirmName(row.name);
    return { row, norm, words: norm.split(' ').filter(Boolean) };
  });
}

/**
 * Bounded optimal-string-alignment distance (Levenshtein + adjacent transposition).
 * Returns max + 1 as soon as the bound cannot be met.
 */
function editDistance(a: string, b: string, max: number): number {
  if (Math.abs(a.length - b.length) > max) return max + 1;
  let prev2: number[] | null = null;
  let prev: number[] = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const cur: number[] = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      let c = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      if (i > 1 && j > 1 && prev2 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        c = Math.min(c, prev2[j - 2] + 1);
      }
      cur[j] = c;
      if (c < rowMin) rowMin = c;
    }
    if (rowMin > max) return max + 1;
    prev2 = prev;
    prev = cur;
  }
  return prev[b.length];
}

function typoTolerance(token: string): number {
  if (token.length < 4) return 0;
  return token.length >= 8 ? 2 : 1;
}

/** True when every token can be assigned to a DIFFERENT word that `matches` it. */
function coversDistinctWords(
  tokens: string[],
  words: string[],
  matches: (token: string, word: string) => boolean,
): boolean {
  const used = new Array<boolean>(words.length).fill(false);
  const assign = (i: number): boolean => {
    if (i === tokens.length) return true;
    for (let w = 0; w < words.length; w++) {
      if (used[w] || !matches(tokens[i], words[w])) continue;
      used[w] = true;
      if (assign(i + 1)) return true;
      used[w] = false;
    }
    return false;
  };
  return assign(0);
}

const prefixMatch = (token: string, word: string): boolean => word.startsWith(token);
const fuzzyMatch = (token: string, word: string): boolean => {
  const max = typoTolerance(token);
  if (max === 0) return word.startsWith(token);
  return editDistance(token, word.slice(0, token.length), max) <= max;
};

/** Tiers 3–4 for rows the prefix tiers did not take. */
function lateTier(p: PreparedFirmName, tokens: string[]): number {
  if (coversDistinctWords(tokens, p.words, prefixMatch)) return 3;
  return coversDistinctWords(tokens, p.words, fuzzyMatch) ? 4 : 0;
}

function byShortestName(a: PreparedFirmName, b: PreparedFirmName): number {
  return a.row.name.length - b.row.name.length || a.row.name.localeCompare(b.row.name);
}

/** Filter + rank a prepared stem list against the full typed text. Empty query → all rows. */
export function rankFirmNames(prepared: PreparedFirmName[], query: string): FirmNameSuggestion[] {
  const q = normalizeFirmName(query);
  // A legal form typed anywhere ("basler ag chemie") is not a matching token.
  const tokens = q.split(' ').filter((t) => t && !LEGAL_FORM_SUFFIXES.includes(t));
  if (tokens.length === 0) return prepared.map((p) => p.row);
  // Tiers 1–2: match-sorter orders by its ranking (STARTS_WITH above WORD_STARTS_WITH),
  // then by our base sort inside a ranking.
  const top = matchSorter(prepared, q, {
    keys: [(p) => p.norm],
    threshold: rankings.WORD_STARTS_WITH,
    baseSort: (a, b) => byShortestName(a.item, b.item),
  });
  const taken = new Set(top);
  const rest: Array<[number, PreparedFirmName]> = [];
  for (const p of prepared) {
    if (taken.has(p)) continue;
    const tier = lateTier(p, tokens);
    if (tier) rest.push([tier, p]);
  }
  rest.sort((a, b) => a[0] - b[0] || byShortestName(a[1], b[1]));
  return [...top, ...rest.map(([, p]) => p)].map((p) => p.row);
}
